import type { RegistrationStatus } from '@contract';
import { Tick02Icon } from '@hugeicons/core-free-icons';
import { Icon } from '@/components/primitives/icon';
import { actionLabel, relativeTime } from '@/lib/format';
import {
  REGISTRATION_STEPS,
  STOP_LABEL,
  STOP_TONE,
  stepsDone,
} from '@/lib/registration';
import { TONE_ALERT_ICON, TONE_CLASSES } from '@/lib/status';
import { cn } from '@/lib/utils';

/**
 * Why an unfinished registration stopped, at a glance (§9.4): the five steps
 * with the stop marked, the reason as one sentence, and the facts behind it.
 */
export function RegistrationCard({
  status,
}: Readonly<{ status: RegistrationStatus }>) {
  const done = stepsDone(status.step);
  const tone = STOP_TONE[status.tone];
  const classes = TONE_CLASSES[tone];
  return (
    <section
      aria-label="Registration"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <ol className="flex items-center gap-1 text-xs" aria-label="Steps">
        {REGISTRATION_STEPS.map((step, index) => {
          const reached = index < done;
          const stopped = index === done;
          return (
            <li
              key={step.key}
              aria-current={stopped ? 'step' : undefined}
              className="flex min-w-0 flex-1 items-center gap-1"
            >
              <span
                className={cn(
                  'grid size-5 shrink-0 place-items-center rounded-full border text-[10px]',
                  reached && 'border-success-border bg-success-tint',
                  stopped && [classes.border, classes.tint],
                )}
              >
                {reached ? (
                  <Icon
                    icon={Tick02Icon}
                    size={14}
                    className="text-success-ink"
                  />
                ) : null}
              </span>
              <span
                className={cn(
                  'truncate',
                  stopped
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {step.label}
                {reached ? <span className="sr-only"> done</span> : null}
                {stopped ? (
                  <span className="sr-only"> stopped here</span>
                ) : null}
              </span>
              {index < REGISTRATION_STEPS.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="mx-1 hidden h-px min-w-3 flex-1 bg-border sm:block"
                />
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="flex items-start gap-2 text-base">
        <Icon
          icon={TONE_ALERT_ICON[tone]}
          size={18}
          className={cn('mt-0.5 shrink-0', classes.ink)}
        />
        <span>
          <span className="font-medium">{STOP_LABEL[status.code]}.</span>{' '}
          <span className="text-muted-foreground">{status.reason}</span>
        </span>
      </p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <Fact label="Checked by" value={providerText(status)} />
        <Fact
          label="Session status"
          value={status.kyc_status ?? 'Never opened'}
        />
        <Fact label="Error code" value={status.error_code} mono />
        <Fact label="Sessions" value={String(status.kyc_sessions)} />
        <Fact label="Restarts" value={String(status.restarts)} />
        <Fact
          label="Last event"
          value={
            status.last_event
              ? `${actionLabel(status.last_event)}, ${relativeTime(status.last_event_at)}`
              : 'None recorded'
          }
        />
        {status.passed_before_restart ? (
          <Fact label="Passed before restart" value="Yes" />
        ) : null}
      </dl>
    </section>
  );
}

function providerText(status: RegistrationStatus): string | null {
  if (status.kyc_provider === 'margins') return 'Margins (NIA)';
  if (status.kyc_provider === 'didit') return 'Didit';
  return null;
}

function Fact({
  label,
  value,
  mono,
}: Readonly<{ label: string; value: string | null; mono?: boolean }>) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('truncate', mono && 'font-mono text-xs')}>
        {value || '—'}
      </dd>
    </div>
  );
}
